import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { label, uploadEvidence, when } from "@/lib/trust";

const get = (url) => () => api.get(url).then((r) => r.data);

function AppealForm({ subjectType, subjectId, onDone }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [files, setFiles] = useState(null);
  if (!open) return <button className="btn-outline text-xs" onClick={() => setOpen(true)} data-testid={`appeal-open-${subjectId}`}>Appeal</button>;
  const send = async () => {
    try {
      const evidence_file_ids = await uploadEvidence(files);
      await api.post("/appeals", { subject_type: subjectType, subject_id: subjectId, explanation: text, evidence_file_ids });
      toast.success("Appeal filed. An independent appeals reviewer will decide it."); onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="mt-2 w-full space-y-2" data-testid={`appeal-form-${subjectId}`}>
      <textarea rows={3} className="field" placeholder="Explain why this decision should change (at least 30 characters)" value={text} onChange={(e) => setText(e.target.value)} data-testid={`appeal-text-${subjectId}`} />
      <input type="file" multiple accept="image/png,image/jpeg,image/webp" onChange={(e) => setFiles(e.target.files)} className="text-xs" data-testid={`appeal-evidence-${subjectId}`} />
      <button className="btn-gold text-xs" onClick={send} disabled={text.trim().length < 30} data-testid={`appeal-submit-${subjectId}`}>Submit appeal</button>
    </div>
  );
}

function InfoReply({ c, onDone }) {
  const [text, setText] = useState("");
  const send = async () => { try { await api.post(`/moderation/cases/${c.case_id}/info`, { text }); toast.success("Information sent to moderators"); onDone(); } catch (err) { toast.error(formatApiError(err)); } };
  return (
    <div className="flex flex-wrap gap-2" data-testid={`info-reply-${c.case_id}`}><span className="text-sm text-slate-300">Moderators asked for more information about “{c.target_label}”.</span>
      <input className="field flex-1" value={text} onChange={(e) => setText(e.target.value)} data-testid={`info-text-${c.case_id}`} />
      <button className="btn-outline text-xs" onClick={send} data-testid={`info-send-${c.case_id}`}>Send</button></div>
  );
}

const Section = ({ title, testid, children }) => <section className="panel space-y-3 p-5" data-testid={testid}><p className="eyebrow">{title}</p>{children}</section>;
const Empty = ({ children, testid }) => <p className="text-sm text-slate-500" data-testid={testid}>{children}</p>;

function Row({ children, testid }) {
  return <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[#2A3447] pt-3 text-sm text-slate-300 first:border-0 first:pt-0" data-testid={testid}>{children}</div>;
}

function Decisions({ mod, apps, vers, appealed, refresh }) {
  const rejected = apps.filter((a) => a.status === "rejected");
  const vDecisions = vers.filter((v) => ["denied", "suspended", "revoked"].includes(v.status));
  const none = !mod.actions.length && !rejected.length && !vDecisions.length;
  return (
    <Section title="Decisions affecting you" testid="my-decisions">
      {mod.actions.map((a) => (
        <Row key={a.action_id} testid={`my-action-${a.action_id}`}>
          <span><span className="font-mono text-xs uppercase text-red-300">{label(a.action)}</span> on {label(a.target_type)} “{a.target_label}” · {when(a.at)}<br /><span className="text-xs text-slate-500">{a.reason}</span></span>
          {a.reversed ? <span className="text-xs text-emerald-300">Reversed</span> : a.appeal ? <span className="text-xs text-aether-300">Appeal {label(a.appeal.status)}</span>
            : <AppealForm subjectType="moderation" subjectId={a.action_id} onDone={refresh} />}
        </Row>
      ))}
      {rejected.map((a) => (
        <Row key={a.application_id} testid={`my-rejected-${a.application_id}`}><span>Application “{a.name}” was rejected</span>
          {appealed.has(a.application_id) ? <span className="text-xs text-aether-300">Appealed</span> : <AppealForm subjectType="application" subjectId={a.application_id} onDone={refresh} />}</Row>
      ))}
      {vDecisions.map((v) => (
        <Row key={v.verification_id} testid={`my-verification-${v.verification_id}`}><span>Verified {v.kind} status for {v.org_name}: {label(v.status)}{v.public_reason && ` — ${v.public_reason}`}</span>
          {appealed.has(v.verification_id) ? <span className="text-xs text-aether-300">Appealed</span> : <AppealForm subjectType="verification" subjectId={v.verification_id} onDone={refresh} />}</Row>
      ))}
      {none && <Empty testid="my-decisions-empty">No moderation, application or verification decisions to appeal.</Empty>}
      <p className="text-xs text-slate-500">One appeal per decision, within 30 days. Appeals are decided by an independent appeals team member who did not make the original decision; an upheld appeal can be escalated once to the platform owner for a final decision.</p>
    </Section>
  );
}

function Appeals({ items, refresh }) {
  const escalate = async (id) => {
    const reason = window.prompt("Why should the owner review this appeal? (min 10 characters)");
    if (!reason) return;
    try { await api.post(`/appeals/${id}/escalate`, { reason }); toast.success("Escalated to the owner"); refresh(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Section title="My appeals" testid="my-appeals">
      {items.map((a) => (
        <Row key={a.appeal_id} testid={`my-appeal-${a.appeal_id}`}>
          <span>{label(a.subject_type)}: {a.original.label} · <span className="font-mono text-xs uppercase text-aether-300" data-testid={`my-appeal-status-${a.appeal_id}`}>{label(a.status)}</span>
            {(a.final || a.decision) && <><br /><span className="text-xs text-slate-500">{(a.final || a.decision).reason}</span></>}</span>
          {a.can_escalate && <button className="btn-outline text-xs" onClick={() => escalate(a.appeal_id)} data-testid={`appeal-escalate-${a.appeal_id}`}>Escalate to owner</button>}
        </Row>
      ))}
      {!items.length && <Empty testid="my-appeals-empty">No appeals filed.</Empty>}
    </Section>
  );
}

export default function MyModeration() {
  const qc = useQueryClient();
  const { data: mod } = useQuery({ queryKey: ["me", "moderation"], queryFn: get("/me/moderation") });
  const { data: appeals } = useQuery({ queryKey: ["me", "appeals"], queryFn: get("/me/appeals") });
  const { data: reports } = useQuery({ queryKey: ["me", "reports"], queryFn: get("/me/reports") });
  const { data: apps } = useQuery({ queryKey: ["me", "applications"], queryFn: get("/me/community-applications") });
  const { data: vers } = useQuery({ queryKey: ["me", "verifications"], queryFn: get("/me/verifications") });
  const refresh = () => qc.invalidateQueries({ queryKey: ["me"] });
  if (!mod || !appeals || !reports || !apps || !vers) return <div className="container-x py-16 text-slate-500">Loading…</div>;
  const appealed = new Set(appeals.items.map((a) => a.subject_id));
  return (
    <div className="container-x space-y-6 py-12" data-testid="my-moderation-page">
      <div><p className="eyebrow">Account standing</p><h1 className="font-display text-4xl text-amber-100 sm:text-5xl">Moderation &amp; appeals</h1></div>
      <Section title="Status" testid="my-account-status">
        <p className="text-sm text-slate-200">Account: <span className="font-mono uppercase text-aether-300" data-testid="my-account-status-value">{mod.account_status}</span>{mod.suspended_until && ` until ${when(mod.suspended_until)}`}</p>
        {mod.account_status === "suspended" && <p className="text-xs text-amber-300">While suspended you can sign in, browse and file appeals only.</p>}
        {mod.restrictions.map((r, i) => <p key={i} className="text-xs text-amber-300" data-testid={`my-restriction-${r.function}`}>Restricted from {label(r.function)} until {when(r.until)}</p>)}
        {mod.warnings.map((w) => <p key={w.warning_id} className={`text-xs ${w.rescinded ? "text-slate-500 line-through" : "text-red-300"}`} data-testid={`my-warning-${w.warning_id}`}>Warning{w.scope !== "platform" ? ` from ${w.scope_name}` : ""} · {when(w.created_at)}: {w.reason}</p>)}
        {mod.info_requested.map((c) => <InfoReply key={c.case_id} c={c} onDone={refresh} />)}
      </Section>
      <Decisions mod={mod} apps={apps.items} vers={vers.items} appealed={appealed} refresh={refresh} />
      <Appeals items={appeals.items} refresh={refresh} />
      <Section title="Reports I filed" testid="my-reports">
        {reports.items.map((r) => <Row key={r.report_id} testid={`my-report-${r.report_id}`}><span>{label(r.category)} · “{r.target_label}”</span><span className="font-mono text-xs uppercase text-aether-300">{label(r.case_status)}</span></Row>)}
        {!reports.items.length && <Empty testid="my-reports-empty">You haven't filed any reports.</Empty>}
      </Section>
    </div>
  );
}
